import { Box, Button, Divider, IconButton, Text } from '@youversion/design-system-components';
import HeartStroked from '@youversion/design-system-icons/lib/HeartStroked';
import XStroked from '@youversion/design-system-icons/lib/XStroked';
import Modal from 'components/ui/modal';
import { setCookie } from 'cookies-next';
import { add, format } from 'date-fns';
import { useQueryParams } from 'hooks/useQueryParams';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useEffect } from 'react';
import { localStorageKeys } from 'utils/constants';
import { firebaseLogEvent } from 'utils/firebase';
import { useAmount } from 'utils/store/amount';
import { useFrequency } from 'utils/store/frequency';
import { usePaymentDate } from 'utils/store/paymentDate';
import { usePaymentMethod } from 'utils/store/paymentMethod';

import ModalFooter from '../modal/Footer';
import ModalHeader from '../modal/Header';

interface Props {
  nextPaymentDate?: string;
  onClose: () => void;
  successMessage: string;
  successTitle: string;
}

export default function ThankYouDialog({ nextPaymentDate, onClose, successMessage, successTitle }: Props) {
  const { setPaymentDate, selectedPaymentDate } = usePaymentDate();
  const { getPaymentMethod } = usePaymentMethod();

  const { t } = useTranslation('common');
  const { selectedFrequency, setFrequency } = useFrequency();
  const { setAmount } = useAmount();
  const router = useRouter();
  const { hide_nav: hideNav } = useQueryParams();
  const isOneTimeGift = selectedFrequency === 'ONETIME';

  async function handleDoneClick() {
    await firebaseLogEvent('GivingButtonTap', { label: 'Done', screen: 'giving_result' });
    setCookie(localStorageKeys.LAST_GIFT_PAYMENT_METHOD, getPaymentMethod());
    await setAmount();
    await setPaymentDate();
    await setFrequency('ONETIME');
    void router.replace(`/give${hideNav ? '?hide_nav=true' : ''}`);
    onClose();
  }

  useEffect(() => void firebaseLogEvent('GivingScreenView', { screen: 'giving_result' }), []);

  return (
    <Modal initial={false} onClose={handleDoneClick}>
      <Box className='flex flex-col justify-between h-full'>
        <ModalHeader
          left={
            <IconButton
              className='inline-end-2 block-start-2'
              data-testid='close-modal-button'
              onClick={handleDoneClick}
            >
              <XStroked className='text-text-light dark:text-text-dark' />
              <label className='sr-only'>{t('giving.cta.close')}</label>
            </IconButton>
          }
          title=''
        />
        <Box className='grow overflow-x-auto flex flex-col pli-3'>
          <Box className='flex flex-col items-center text-center plb-2'>
            <HeartStroked className='min-w-[56px] min-h-[56px] text-yv-red' />
            <Text className='p-1' size='lg' variant='headline'>
              {successTitle ?? t('giving.titles.thank_you')}
            </Text>
            <Text className='p-1' size='lg' variant='caption'>
              {successMessage}
            </Text>
          </Box>
          {isOneTimeGift && getPaymentMethod() ? (
            <Box>
              <Divider className='border-small' />
              <Box className='flex flex-col items-center text-center plb-2'>
                <Text className='p-1'>{t('giving.messages.onetime_recurring')}</Text>
                <Button
                  className='m-1'
                  id='btn_schedule_now'
                  onClick={() => {
                    firebaseLogEvent('GivingButtonTap', { label: 'Schedule Now', screen: 'giving_result' });
                    setCookie(localStorageKeys.LAST_GIFT_PAYMENT_METHOD, getPaymentMethod());
                    setFrequency('MONTHLY');
                    setPaymentDate(add(selectedPaymentDate, { months: 1 }));
                    onClose();
                  }}
                >
                  <Text className='text-text-dark dark:text-text-light'>{t('giving.cta.make_it_recurring')}</Text>
                </Button>
              </Box>
            </Box>
          ) : (
            <Box className='flex flex-col items-center text-center'>
              <Text size='md' variant='paragraph'>
                {nextPaymentDate
                  ? `${t('giving.titles.next_gift')}: ${format(Date.parse(nextPaymentDate), 'LLL d, yyyy')}`
                  : null}
              </Text>
            </Box>
          )}
        </Box>
        <ModalFooter buttonText={t('giving.cta.done')} onClick={handleDoneClick} />
      </Box>
    </Modal>
  );
}
